// Shared Web-Speech helpers for the AI assistants (Ask FR + Family Assistant).
// Lets the user pick a Male or Female voice. Browser voice availability varies
// wildly, so we (1) try to match a real voice by name/lang and (2) ALWAYS shape
// the pitch by gender so the difference is audible even when only one system
// voice exists.

const FEMALE_HINTS = /(female|woman|zira|susan|samantha|karen|tessa|veena|lekha|heera|kalpana|neerja|aditi|swara|priya|isha|google हिन्दी|google hindi)/i;
const MALE_HINTS = /(\bmale\b|\bman\b|david|mark|alex|daniel|fred|ravi|hemant|madhur|prabhat|rishi)/i;

const VOICE_KEY = "fr_voice_gender";

// App language code → BCP-47 tag for Web-Speech recognition & synthesis.
const STT_LANG = {
    en: "en-IN", hi: "hi-IN", mr: "mr-IN", ta: "ta-IN", bn: "bn-IN",
    gu: "gu-IN", te: "te-IN", kn: "kn-IN", ml: "ml-IN", pa: "pa-IN",
};
export const sttLang = (code) => STT_LANG[code] || "hi-IN";

export const getVoiceGender = () => {
    try { return localStorage.getItem(VOICE_KEY) === "male" ? "male" : "female"; }
    catch { return "female"; }
};

export const setVoiceGenderPref = (g) => {
    try { localStorage.setItem(VOICE_KEY, g === "male" ? "male" : "female"); } catch { /* ignore */ }
};

// Nudge browsers (esp. Chrome) to populate the async voice list early.
export function warmUpVoices() {
    try {
        if (typeof window === "undefined" || !window.speechSynthesis) return;
        window.speechSynthesis.getVoices();
        window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    } catch { /* ignore */ }
}

function pickVoice(gender, langBcp47) {
    if (typeof window === "undefined" || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    // Prefer voices for the requested language; else any Indic voice; else all.
    const prefix = (langBcp47 || "").split("-")[0].toLowerCase();
    const langMatch = prefix ? voices.filter((v) => v.lang.toLowerCase().startsWith(prefix)) : [];
    const indic = voices.filter((v) => /hi-IN|mr-IN|en-IN|ta-IN|bn-IN|gu-IN|te-IN|kn-IN|ml-IN|pa-IN/i.test(v.lang));
    const pool = langMatch.length ? langMatch : (indic.length ? indic : voices);
    const want = gender === "male" ? MALE_HINTS : FEMALE_HINTS;
    const avoid = gender === "male" ? FEMALE_HINTS : MALE_HINTS;
    // 1) a voice whose name matches the wanted gender
    let match = pool.find((v) => want.test(v.name));
    // 2) else any voice that is NOT clearly the opposite gender
    if (!match) match = pool.find((v) => !avoid.test(v.name));
    return match || pool[0] || null;
}

export function speakText(text, gender = "female", langBcp47 = "hi-IN") {
    try {
        if (typeof window === "undefined" || !window.speechSynthesis || !text) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(String(text));
        const v = pickVoice(gender, langBcp47);
        if (v) { u.voice = v; }
        u.lang = (v && v.lang) || langBcp47 || "hi-IN";
        u.rate = 1.02;
        // Pitch guarantees an audible male/female difference regardless of the
        // actual system voice picked.
        u.pitch = gender === "male" ? 0.72 : 1.18;
        window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
}
